import { differenceInCalendarDays } from 'date-fns';
import useSWR from 'swr';
import { useShop } from '@services/ShopProvider';
import { useEnabledChannels } from '@services/channels';
import { listAllPlans } from '@services/localAds';
import { useHasAdsInPeriod } from '../../../hooks/useHasAdsInPeriod';

/**
 * Empty-state classifier for Overview cards. Returns one of four states based
 * on whether the current shop(s) are set up, whether the data for this card is
 * still warming up, whether there's no traction yet, or whether real data is
 * present.
 *
 * NEA-5635 — replaces the naive `<NoData text="No data available yet for this week" />`
 * branch that gives day-zero retailers a flat impression.
 */

export const CARD_STATES = {
	SETUP: 'setup',
	PENDING: 'pending',
	NO_TRACTION: 'no-traction',
	DATA: 'data',
	// Not returned by the classifier — cards set this themselves when their data
	// fetch fails, so an API error reads as "couldn't load" rather than "no data".
	ERROR: 'error'
};

const SETUP_COPY = {
	inventory: {
		copy: 'Connect your inventory to start seeing data.',
		ctaLabel: 'Connect inventory',
		ctaHref: '/onboarding'
	},
	google: {
		copy: 'Enable Google to see local listings data.',
		ctaLabel: 'Set up Google',
		ctaHref: '/channels'
	},
	'product-locator': {
		copy: 'Enable Product Locator to see store visit data.',
		ctaLabel: 'Set up Product Locator',
		ctaHref: '/channels'
	},
	ads: {
		copy: 'Run Local Ads to see ads performance.',
		ctaLabel: 'Run Local Ads',
		ctaHref: '/channels'
	}
};

const PENDING_COPY = {
	localListings: 'Listings populate within 24-48h after Google enable.',
	organicVisibility: 'Organic visibility data accumulates over the first week.',
	localAds: 'Local Ads data appears as soon as a plan goes live.',
	productLocator: 'Product Locator data appears within 24-48h.'
};

const NO_TRACTION_COPY = 'No data for this period. Try widening the date range.';

const latestIngestAcrossShops = (shops) => {
	const dates = (shops ?? [])
		.map((shop) => shop.stockInventorySource?.latestIngest?.timestamp)
		.filter(Boolean)
		.map((t) => new Date(t));
	if (!dates.length) return null;
	return dates.reduce((latest, d) => (latest > d ? latest : d), dates[0]);
};

/**
 * @param {object} config
 * @param {'localListings'|'organicVisibility'|'localAds'|'productLocator'} config.cardId — keys into PENDING_COPY
 * @param {'google'|'product-locator'|null} [config.requiresChannel] — channel that must be enabled (in `useEnabledChannels`) for this card to show data
 * @param {boolean} [config.requiresAds] — true if the card needs an active ad plan in the period
 * @param {number} [config.minDaysForData] — days since latest ingest below which we show "pending" copy. 0 disables the pending check.
 * @param {boolean} [config.dataIsEmpty] — caller passes `!data?.length` after their own data hook resolves
 * @returns {{ state: string, copy: string|null, ctaLabel: string|null, ctaHref: string|null }}
 */
export const useCardEmptyState = ({ cardId, requiresChannel = null, requiresAds = false, minDaysForData = 0, dataIsEmpty = false }) => {
	const { data: shops, shop } = useShop();
	const enabledChannels = useEnabledChannels();
	// Scope follows the cards: single-store view (URL has shopId) inspects only
	// that shop; chain view (no shopId) inspects the whole org.
	const scopeShops = shop?.id ? [shop] : (shops ?? []);
	const shopIds = (shops ?? []).map((s) => s.id);
	const adsInPeriod = useHasAdsInPeriod(shopIds);
	// Shared SWR cache with usePlansForShopIds (same key) — lets us tell
	// "still loading" apart from "no plans" so requiresAds doesn't fire a
	// false SETUP while plans are arriving.
	const { data: allPlans } = useSWR('local-ads-all-plans', listAllPlans);

	// Treat undefined dep results as "not ready" rather than "empty" — otherwise
	// the card flashes SETUP for one frame before channels / plans resolve, even
	// for retailers who have everything enabled.
	const channelsPending = requiresChannel && enabledChannels === undefined;
	const adsPending = requiresAds && allPlans === undefined;
	if (channelsPending || adsPending) {
		return { state: CARD_STATES.DATA, copy: null, ctaLabel: null, ctaHref: null };
	}

	const anyShopConnected = scopeShops.some((s) => Boolean(s.stockInventorySource?.latestIngest?.timestamp));
	if (!anyShopConnected) {
		const { copy, ctaLabel, ctaHref } = SETUP_COPY.inventory;
		return { state: CARD_STATES.SETUP, copy, ctaLabel, ctaHref };
	}

	if (requiresChannel) {
		const isChannelEnabled = enabledChannels.some((c) => c.id === requiresChannel);
		if (!isChannelEnabled) {
			const setup = SETUP_COPY[requiresChannel] ?? SETUP_COPY.inventory;
			return { state: CARD_STATES.SETUP, copy: setup.copy, ctaLabel: setup.ctaLabel, ctaHref: setup.ctaHref };
		}
	}

	if (requiresAds && !adsInPeriod) {
		const { copy, ctaLabel, ctaHref } = SETUP_COPY.ads;
		return { state: CARD_STATES.SETUP, copy, ctaLabel, ctaHref };
	}

	// PENDING means "this shop hasn't been ingesting long enough for the data
	// to have accumulated yet" — only fire if the SCOPED shop is the new one.
	// For chain view, looking at org-wide latestIngest produces false positives
	// because some sibling shop will almost always have ingested recently —
	// the chain has been operating for years, so PENDING is misleading.
	if (dataIsEmpty && minDaysForData > 0 && scopeShops.length === 1) {
		const latest = latestIngestAcrossShops(scopeShops);
		if (latest) {
			const daysSinceIngest = differenceInCalendarDays(new Date(), latest);
			if (daysSinceIngest < minDaysForData) {
				return {
					state: CARD_STATES.PENDING,
					copy: PENDING_COPY[cardId] ?? null,
					ctaLabel: null,
					ctaHref: null
				};
			}
		}
	}

	if (dataIsEmpty) {
		return { state: CARD_STATES.NO_TRACTION, copy: NO_TRACTION_COPY, ctaLabel: null, ctaHref: null };
	}

	return { state: CARD_STATES.DATA, copy: null, ctaLabel: null, ctaHref: null };
};
